import { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $createParagraphNode,
  $createTextNode,
} from "lexical";
import { $generateNodesFromDOM } from "@lexical/html";
import { $createVariableNode } from "./VariableNode";

const VARIABLE_REGEX = /\{\{([^}]+)\}\}/g;

function parseLineToNodes(line, variableLookup) {
  const nodes = [];
  let lastIndex = 0;
  let match;

  VARIABLE_REGEX.lastIndex = 0;
  while ((match = VARIABLE_REGEX.exec(line)) !== null) {
    if (match.index > lastIndex) {
      nodes.push($createTextNode(line.slice(lastIndex, match.index)));
    }

    const varName = match[1].trim();
    const displayName =
      variableLookup[varName] || varName.replace(/_/g, " ");
    nodes.push($createVariableNode(varName, displayName));

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < line.length) {
    nodes.push($createTextNode(line.slice(lastIndex)));
  }

  if (nodes.length === 0) {
    nodes.push($createTextNode(""));
  }

  return nodes;
}

function isHTML(content) {
  return /<[a-z][\s\S]*>/i.test(content);
}

/**
 * Replace {{variable}} text nodes with VariableNode chips
 * after DOM-based import.
 */
function replaceVariablesInTree(node, variableLookup) {
  const children = node.getChildren ? node.getChildren() : [];
  for (const child of children) {
    if (child.getType() === "text") {
      const text = child.getTextContent();
      if (!VARIABLE_REGEX.test(text)) continue;

      // Split text and replace with variable nodes
      VARIABLE_REGEX.lastIndex = 0;
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = VARIABLE_REGEX.exec(text)) !== null) {
        if (match.index > lastIndex) {
          const before = $createTextNode(text.slice(lastIndex, match.index));
          // Preserve formatting from original node
          before.setFormat(child.getFormat());
          parts.push(before);
        }
        const varName = match[1].trim();
        const displayName =
          variableLookup[varName] || varName.replace(/_/g, " ");
        parts.push($createVariableNode(varName, displayName));
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        const after = $createTextNode(text.slice(lastIndex));
        after.setFormat(child.getFormat());
        parts.push(after);
      }

      if (parts.length > 0) {
        for (const part of parts) {
          child.insertBefore(part);
        }
        child.remove();
      }
    } else if (child.getChildren) {
      replaceVariablesInTree(child, variableLookup);
    }
  }
}

function initializeFromHTML(editor, content, variableLookup) {
  editor.update(() => {
    const root = $getRoot();
    root.clear();

    const parser = new DOMParser();
    const dom = parser.parseFromString(content, "text/html");
    const nodes = $generateNodesFromDOM(editor, dom);

    if (nodes.length > 0) {
      root.append(...nodes);
    }

    // Replace {{...}} text with VariableNodes
    replaceVariablesInTree(root, variableLookup);
  });
}

function initializeFromPlainText(editor, content, variableLookup) {
  editor.update(() => {
    const root = $getRoot();
    root.clear();

    const lines = content.split("\n");
    for (const line of lines) {
      const paragraph = $createParagraphNode();
      const nodes = parseLineToNodes(line, variableLookup);
      for (const node of nodes) {
        paragraph.append(node);
      }
      root.append(paragraph);
    }
  });
}

const VariablePlugin = ({ initialContent, variables }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (initialContent === undefined || initialContent === null) return;

    const variableLookup = {};
    if (variables) {
      for (const v of variables) {
        variableLookup[v.name] = v.display_name || v.name;
      }
    }

    if (isHTML(initialContent)) {
      initializeFromHTML(editor, initialContent, variableLookup);
    } else {
      initializeFromPlainText(editor, initialContent, variableLookup);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default VariablePlugin;
